import React, { useEffect, useState } from "react"
import {
  getAccountingCSV,
  getAccountingDashboard,
  getAccountingPayments
} from "../../../../../features/accounting/accounting"
import { showSnackBar } from "../../../../../components/Snackbar/Snackbar"
import { useDispatch } from "react-redux"
import { useMyContextLoading } from "../../../../../components/LoadingContextProvider/LoadingContextProvider"
import TableFooter from "../../../../../components/Pagination/TableFooter"
import TableList from "../TableList"
import { PaymentPlanIcon } from "../../../../../components/Icons/Icons"
import { formatMoney } from "../../../../../utils/amount"
import Filters from "../../../../../components/Filters/Filters"
import { initQueryData } from "../../../../../components/Filters/helper"
import Button from "../../../../../components/Button/Button"
import { TABLE_CARD } from "../../../../../components/Table/tableCardClass"
import AccountingDailyEmailSwitch from "../../../../../components/AccountingDailyEmailSwitch/AccountingDailyEmailSwitch"


const Payments = ({ userRole }) => {
  const dispatch = useDispatch()
  const { triggerLoading, triggerBar } = useMyContextLoading()

  const [valuePagination, setValuePagination] = useState(10)
  const [pagePagination, setPagePagination] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const [totalCount, setTotalCount] = useState(null)
  const [payments, setPayments] = useState([])
  const [requestFilters, setRequestFilters] = useState([initQueryData])
  const [dashboardData, setDashboardData] = useState({
    cleared: 0,
    processing: 0,
    failed: 0,
    scheduled: 0,
  })

  const getAccountingCSVFunc = async () => {
    triggerLoading(true)
    try {
      const hasFilters = requestFilters?.length > 0 && requestFilters.some(f => f.value || f.operator)
      let filters = hasFilters ? requestFilters : ""

      let result = await dispatch(getAccountingCSV({ filters }))
      const base64Data = result?.payload?.data

      if (!base64Data) throw new Error('No data received')

      const link = document.createElement('a')
      link.href = base64Data
      link.download = `accounting_export.csv`
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading CSV file:', error)
    }
    triggerLoading(false)
  }

  const getAcPayments = async () => {
    triggerBar(true)
    try {
      const limit = parseInt(valuePagination)
      const offset = (pagePagination - 1) * limit
      const hasFilters = requestFilters?.length > 0 && requestFilters.some(f => f.value || f.operator)
      let filters = hasFilters ? requestFilters : ""

      let result = await dispatch(getAccountingPayments({
        limit: valuePagination,
        offset,
        filters
      }))
      if (result?.error) {
        const errorMessage = result?.payload || 'An error occurred. Please try again.'
        showSnackBar("error", errorMessage)
      } else if (result?.payload?.data) {
        setPayments(result?.payload?.data?.payments)
        setPageCount(result?.payload?.data?.meta?.pageCount)
        setTotalCount(result?.payload?.data?.meta?.totalPayments)
      }
    } catch
    (error) {
      showSnackBar('error', 'An unexpected error occurred. Please try again.')
    }
    triggerBar(false)
  }

  const fetchDashboardData = async () => {
    try {
      const hasFilters = requestFilters?.length > 0 && requestFilters.some(f => f.value || f.operator)
      let filters = hasFilters ? requestFilters : ""

      let result = await dispatch(getAccountingDashboard({ filters }))
      if (result?.error) {
        const errorMessage = result?.payload || 'An error occurred. Please try again.'
        showSnackBar("error", errorMessage)
      } else if (result?.payload?.data) {
        setDashboardData(result?.payload?.data)
      }
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred. Please try again.')
    }
  }

  const fetch = () => {
    getAcPayments()
    fetchDashboardData()
  }

  // Filter and rows-per-page changes keep the current page — the Pagination
  // component clamps to the last page when the new page count is smaller, so
  // the operator never lands past the end (and never gets yanked back to page 1).
  const applyFilters = (newFilters) => {
    setRequestFilters(newFilters)
  }

  useEffect(() => {
    fetch()
  }, [pagePagination, valuePagination, requestFilters])

  const isValidNumber = (value) => !isNaN(parseFloat(value)) && isFinite(value)

  return (
    <div>
      <div className="flex flex-row justify-between items-center w-full">
        <Filters isFilterList={false} fetchByFilters={applyFilters}
          requestFilters={requestFilters} flow="payments" />

        <div className="bg-surface border border-divider rounded-2xl shadow-custom flex flex-row  my-4 py-4 max-w-max">
          <div className="flex px-4 gap-4 items-center border-r-2 border-dashed border-divider">
            <PaymentPlanIcon bgFill="var(--color-info-background)" fill="var(--color-info-main)" />
            <div className="flex flex-col">
              <span className="font-bold text-lg-18 mb-1">
                {formatMoney(isValidNumber(dashboardData?.scheduled)
                  ? dashboardData?.scheduled
                  : 0)
                }
              </span>
              <span className="text-md">Total Pending</span>
            </div>
          </div>

          <div className="flex px-4 gap-4 border-r-2 border-dashed border-divider items-center">
            <PaymentPlanIcon bgFill="var(--color-success-200)" fill="var(--color-success-600)" />
            <div className="flex flex-col">
              <span className="font-bold text-lg-18 mb-1">
                {formatMoney(isValidNumber(dashboardData?.cleared)
                  ? dashboardData?.cleared
                  : 0)
                }
              </span>
              <span className="text-md">Total Paid</span>
            </div>
          </div>
          <div className="flex px-4 gap-4 border-r-2 border-dashed border-divider items-center">
            <PaymentPlanIcon bgFill="var(--color-warning-200)" fill="var(--color-warning-600)" />
            <div className="flex flex-col">
              <span className="font-bold text-lg-18 mb-1">
                {formatMoney(isValidNumber(dashboardData?.processing)
                  ? dashboardData?.processing
                  : 0)
                }
              </span>
              <span className="text-md">Total Processing</span>
            </div>
          </div>
          <div className="flex px-4 gap-4 items-center">
            <PaymentPlanIcon />
            <div className="flex flex-col">
              <span className="font-bold text-lg-18 mb-1">
                {formatMoney(isValidNumber(dashboardData?.failed)
                  ? dashboardData?.failed
                  : 0)
                }
              </span>
              <span className="text-md">Total Failed</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end mb-4">
        <div className="flex items-center gap-4">
          <Button type={"outlined"} onClick={getAccountingCSVFunc} classes={"border !border-primary-main h-10"}>
            <p className={`flex`}> Export In CSV </p>
          </Button>
          <AccountingDailyEmailSwitch />
        </div>
      </div>

      <div className={TABLE_CARD}>
        <TableList triggerUpdate={(e) => {
          e && fetch()
        }} data={payments} userRole={userRole} />
        <TableFooter page={pagePagination} limit={valuePagination} total={totalCount}
          pageCount={pageCount} setPage={setPagePagination} setLimit={setValuePagination} />
      </div>
    </div>
  )
}

export default Payments